// Card 18, coastal profiles — the outline on the map (R548, R1821,
// ui-map-interface.md §17.3.18).
//
// The old chart's margin shows a coast as a sailor sees it from the sea, so
// the landfall can be recognised. The profile is two halves:
//
//   * this piece, which outlines the hovered ridge on the water with a
//     dashed line, so the reader knows which body the peek is about;
//   * the body of the ridge peek, `ui/cards/terrain.ts`, which draws the
//     elevation along the ridge inside the card kit's peek (round 086 card
//     06 b). It is chrome and no piece, so no second card opens.
//
// Both read `CHART.pieces.coastal_profile`: the outline its gate and its
// dash, the peek the figures of its drawing.
//
// **THE HOVER IS THE TOOLTIP'S.** `ctx.hoverCell` is the cell under the
// pointer, and on touch it is the cell the tooltip's long-press peek pinned
// (`fast/tooltip.ts`). The piece therefore needs no pointer code of its own,
// and the owner's ruling — hover on desktop, the peek on touch — is kept by
// the one field.

import { CHART } from '../../../gen/presentation';
import type { Feature } from '../../../render/terrain-features';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { CHART_INK, dashedLoop } from './shared';

/** The figures the piece reads out of its own table. */
const T = CHART.pieces.coastal_profile;

/**
 * The ridge the pointer stands on, or null.
 *
 * The walk is over the ridges of the world and their cell lists. The piece
 * keeps the answer for its cell, so a hover change pays the walk once.
 */
export function hoveredRidge(ctx: ChartContext): Feature | null {
  const terrain = ctx.terrain;
  if (!terrain || ctx.hoverCell < 0) return null;
  for (const f of terrain.ridges) {
    if (f.cells.includes(ctx.hoverCell)) return f;
  }
  return null;
}

export class CoastalOutlinePiece implements Piece {
  readonly id = 'coastal_profile' as const;
  /** Over the fleets: the outline says which body the peek is about. */
  readonly tier = 'screen' as const;
  readonly pack = 'static' as const;
  /** It belongs to the pointer of the season running now, so a film hides it. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hover', 'hello', 'reading', 'zoomband'] as const;

  private loops: readonly (readonly [number, number])[][] = [];
  /**
   * The ridge under the pointer, and the cell and the terrain it was found
   * for. The gate asks on every frame, and the walk runs only when the cell
   * or the world moved.
   */
  private ridge: Feature | null = null;
  private ridgeCell = -1;
  private ridgeTerrain: ChartContext['terrain'] = null;

  visible(ctx: ChartContext): boolean { return this.hovered(ctx) !== null; }

  rebuild(ctx: ChartContext): void {
    const f = this.hovered(ctx);
    this.loops = f ? f.outline.filter((l) => l.length >= 3) : [];
  }

  /** `hoveredRidge`, walked again only for a new cell or a new world. */
  private hovered(ctx: ChartContext): Feature | null {
    if (ctx.hoverCell !== this.ridgeCell || ctx.terrain !== this.ridgeTerrain) {
      this.ridgeCell = ctx.hoverCell;
      this.ridgeTerrain = ctx.terrain;
      this.ridge = hoveredRidge(ctx);
    }
    return this.ridge;
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const style = ctx.cfg.pieces.coastal_profile[ctx.reading];
    for (const loop of this.loops) {
      dashedLoop(
        sinks.segments, loop, style.width, CHART_INK.pale, T.fig.outline_alpha,
        T.arr.outline_dash,
      );
    }
  }

  /** How many loops the outline holds now. The test reads it. */
  get loopCount(): number { return this.loops.length; }
}

/** The one instance the registry holds. */
export const coastalOutline = new CoastalOutlinePiece();
